import { supabase } from '../client';
import type { Database, Json } from '../generated';

// ---- types ---------------------------------------------------------------

export type ProviderCatalogEntry = Database['core']['Tables']['provider_catalog']['Row'];

/**
 * How a connection is shared across an org's stores. Mirrors the
 * `core.connection_share_mode` enum
 * (`20260703260050_integrations_share_mode_and_grants.sql`).
 */
export type ShareMode = 'all_stores' | 'selected_stores' | 'single_store';

/**
 * Generated types lag the `share_mode` column, so we intersect it on. See
 * `20260703260050_integrations_share_mode_and_grants.sql`.
 */
export type ProviderConnection = Database['core']['Tables']['org_provider_connections']['Row'] & {
  share_mode: ShareMode;
};

/** A `selected_stores` grant row (`core.connection_store_grants`). */
export interface ConnectionStoreGrant {
  org_id: string;
  connection_id: string;
  store_id: string;
  created_at: string;
}

/**
 * Store-aware capability routing row (`core.capability_routing`). `store_id`
 * NULL = the org default applied to every store; non-NULL = a per-store
 * override. `fallback_connection_id` is the config-time fallback.
 */
export interface CapabilityRouting {
  id: string;
  org_id: string;
  store_id: string | null;
  capability: string;
  primary_connection_id: string | null;
  fallback_connection_id: string | null;
  updated_at: string;
  archived_at: string | null;
}

/** One ranked survivor from `core.fn_resolve_capability_connection`. */
export interface ResolvedConnection {
  connection_id: string;
  rank: number;
  source: 'primary' | 'fallback' | 'available';
}

/**
 * Declarative field spec attached to each provider in the catalog.
 * The Integrations UI is rendered entirely from this shape -- adding a new
 * provider takes one row + one verifier branch in the Edge Function.
 */
export interface ProviderField {
  id: string;
  label: string;
  type: 'public' | 'secret';
  help?: string;
  placeholder?: string;
  optional?: boolean;
  choices?: string[];
  default?: string;
}
export interface ProviderFieldsBlob {
  fields: ProviderField[];
}

// ---- catalog -------------------------------------------------------------

export async function listProviderCatalog(): Promise<ProviderCatalogEntry[]> {
  const { data, error } = await supabase()
    .schema('core')
    .from('provider_catalog')
    .select('*')
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .limit(200);
  if (error) throw error;
  return data ?? [];
}

// ---- connections ---------------------------------------------------------

export async function listOrgConnections(orgId: string): Promise<ProviderConnection[]> {
  const { data, error } = await supabase()
    .schema('core')
    .from('org_provider_connections')
    .select('*')
    .eq('org_id', orgId)
    .order('created_at', { ascending: true })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as unknown as ProviderConnection[];
}

export interface SaveConnectionInput {
  connectionId?: string;
  orgId?: string;
  providerId?: string;
  displayName?: string | null;
  /** Non-secret metadata, e.g. publishable keys. */
  metadata?: Record<string, unknown>;
  /** Secret fields only. Written to Supabase Vault. */
  secrets?: Record<string, string>;
  testMode?: boolean;
  /** Sharing scope. Omit to leave unchanged (defaults to all_stores on create). */
  shareMode?: ShareMode;
  /** Required when shareMode is `single_store`. */
  storeId?: string | null;
}

export async function saveProviderConnection(input: SaveConnectionInput): Promise<string> {
  const { data, error } = await supabase()
    .schema('core')
    // Generated types lag the new p_store_id / p_share_mode params.
    .rpc('rpc_save_provider_connection', {
      p_connection_id: input.connectionId ?? undefined,
      p_org_id: input.orgId ?? undefined,
      p_provider_id: input.providerId ?? undefined,
      p_display_name: input.displayName ?? undefined,
      p_metadata: (input.metadata ?? {}) as Json,
      p_secrets: (input.secrets ?? null) as Json | null,
      p_test_mode: input.testMode ?? false,
      p_store_id: input.storeId ?? undefined,
      p_share_mode: input.shareMode ?? undefined,
    } as never);
  if (error) throw error;
  return data as unknown as string;
}

export async function disconnectProvider(connectionId: string): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    .rpc('rpc_disconnect_provider', { p_connection_id: connectionId });
  if (error) throw error;
}

// ---- selected-store grants ----------------------------------------------

/** All selected-store grants for an org (one row per connection+store). */
export async function listConnectionStoreGrants(orgId: string): Promise<ConnectionStoreGrant[]> {
  const { data, error } = await (supabase() as never as ReturnType<typeof supabase>)
    .schema('core')
    .from('connection_store_grants' as never)
    .select('*')
    .eq('org_id', orgId)
    .limit(2000);
  if (error) throw error;
  return (data ?? []) as unknown as ConnectionStoreGrant[];
}

/**
 * Reconcile the set of stores a connection is shared with. Flips the
 * connection to `selected_stores` and writes the grant rows. Backed by
 * `core.rpc_set_connection_store_grants`.
 */
export async function setConnectionStoreGrants(
  connectionId: string,
  storeIds: string[],
): Promise<void> {
  // Generated types lag this RPC; cast the schema client to call it.
  const { error } = await (supabase().schema('core') as unknown as {
    rpc: (name: string, args: Record<string, unknown>) => Promise<{ error: unknown }>;
  }).rpc('rpc_set_connection_store_grants', {
    p_connection_id: connectionId,
    p_store_ids: storeIds,
  });
  if (error) throw error;
}

// ---- capability routing -------------------------------------------------

/** Active routing rows for an org (org-default + per-store overrides). */
export async function listCapabilityRouting(orgId: string): Promise<CapabilityRouting[]> {
  const { data, error } = await (supabase() as never as ReturnType<typeof supabase>)
    .schema('core')
    .from('capability_routing' as never)
    .select('*')
    .eq('org_id', orgId)
    .is('archived_at', null)
    .limit(2000);
  if (error) throw error;
  return (data ?? []) as unknown as CapabilityRouting[];
}

export interface SetRoutingOptions {
  /** Config-time fallback. */
  fallbackConnectionId?: string | null;
  /** NULL/undefined = org default; a store id = per-store override. */
  storeId?: string | null;
}

export async function setCapabilityRouting(
  orgId: string,
  capability: string,
  primaryConnectionId: string | null,
  options: SetRoutingOptions = {},
): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    .rpc('rpc_set_capability_routing', {
      p_org_id: orgId,
      p_capability: capability,
      // The SQL function accepts NULL to mean "unset routing"; the
      // generated TS type is non-nullable so we widen via undefined
      // (which PostgREST encodes the same way as a missing param,
      // which Postgres resolves to the function's NULL default).
      p_connection_id: primaryConnectionId ?? (undefined as unknown as string),
      p_fallback_connection_id: options.fallbackConnectionId ?? undefined,
      p_store_id: options.storeId ?? undefined,
    } as never);
  if (error) throw error;
}

/**
 * Debug/read helper: resolve which connections would serve (org, store,
 * capability), ranked primary -> fallback -> available. The first row is what
 * a runtime caller uses. Backed by `core.fn_resolve_capability_connection`.
 */
export async function resolveCapabilityConnection(
  orgId: string,
  storeId: string | null,
  capability: string,
): Promise<ResolvedConnection[]> {
  // Generated types lag this function; cast the schema client to call it.
  const { data, error } = await (supabase().schema('core') as unknown as {
    rpc: (name: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
  }).rpc('fn_resolve_capability_connection', {
    p_org_id: orgId,
    p_store_id: storeId ?? undefined,
    p_capability: capability,
  });
  if (error) throw error;
  return (data ?? []) as unknown as ResolvedConnection[];
}

// ---- verify --------------------------------------------------------------

export interface VerifyResult {
  ok: boolean;
  message?: string;
}

/**
 * Calls the `provider-verify` Edge Function with the caller's JWT. The
 * function reads the connection's secrets from Vault (service-role only)
 * and runs the provider's verifier, then stamps the verification status
 * on the connection row.
 */
export async function verifyProviderConnection(connectionId: string): Promise<VerifyResult> {
  const { data, error } = await supabase().functions.invoke<VerifyResult>('provider-verify', {
    body: { connection_id: connectionId },
  });
  if (error) {
    return { ok: false, message: error.message };
  }
  return data ?? { ok: false, message: 'No response from verifier.' };
}

// ---- org role helpers (for UI gating) -----------------------------------

export type OrgRole = 'owner' | 'admin' | 'operator' | 'viewer';

export async function getMyRoleInOrg(orgId: string): Promise<OrgRole | null> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('user_role_in_org', { p_org_id: orgId });
  if (error) return null;
  return (data as unknown as OrgRole | null) ?? null;
}

export async function isMyOrgAdmin(orgId: string): Promise<boolean> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('user_is_org_admin', { p_org_id: orgId });
  if (error) return false;
  return Boolean(data);
}

/**
 * Pure predicates over `OrgRole`. Centralized here (and not duplicated in
 * `apps/web/src/settings/useMyOrgRole.ts`) so every consumer — settings
 * pages, operational modules, the shared shell banner — agrees on the
 * mapping. Mirrors the DB helpers:
 *   - `isAdminRole`   ↔ `core.user_is_org_admin`
 *   - `canWriteRole`  ↔ `core.user_can_write_org`
 *   - `isViewerRole`  ↔ NOT canWriteRole
 *
 * Source of truth for the boundary lives in
 * `peptideclients/supabase/migrations/20260702060000_role_writer_gate.sql`.
 * Any change to which roles count as writers MUST land in BOTH places.
 */
export function isAdminRole(role: OrgRole | null | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

export function canWriteRole(role: OrgRole | null | undefined): boolean {
  return role === 'owner' || role === 'admin' || role === 'operator';
}

export function isViewerRole(role: OrgRole | null | undefined): boolean {
  return role === 'viewer';
}
